import { useState } from "react";
import { IconArrowDown, IconCheck, IconCopy, IconDownload } from "@tabler/icons-react";
import { Button, buttonVariants } from "@/components/wensity/button";
import { rawScriptPath, type ContentItem } from "@/data/site";

export function ScriptActions({ item, fullScriptId }: { item: ContentItem; fullScriptId: string }) {
  const [copied, setCopied] = useState(false);

  const rawUrl = rawScriptPath(item);
  const absoluteRawUrl = `${window.location.origin}${rawUrl}`;
  const iwrCommand = `iwr ${absoluteRawUrl} | iex`;

  async function handleCopy() {
    try {
      await navigator.clipboard.writeText(iwrCommand);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard access can be denied by the browser — nothing to recover
      // from here, the command is still visible to copy manually.
    }
  }

  function handleJump() {
    document.getElementById(fullScriptId)?.scrollIntoView({ behavior: "smooth", block: "start" });
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-3">
        <a href={rawUrl} download={`${item.slug}.ps1`} className={buttonVariants({ variant: "outline" })}>
          <IconDownload size={16} className="mr-1.5" aria-hidden="true" />
          Raw .ps1
        </a>
        <Button variant="outline" onClick={handleCopy}>
          {copied ? (
            <IconCheck size={16} className="mr-1.5 text-[var(--color-primary)]" aria-hidden="true" />
          ) : (
            <IconCopy size={16} className="mr-1.5" aria-hidden="true" />
          )}
          {copied ? "Copied!" : "Copy Remote Script"}
        </Button>
        <Button variant="ghost" onClick={handleJump}>
          <IconArrowDown size={16} className="mr-1.5" aria-hidden="true" />
          Jump to Full Script
        </Button>
      </div>

      <div className="space-y-3 rounded-xl border border-[var(--color-border)] bg-black/40 p-4">
        <p className="text-xs uppercase tracking-[0.14em] text-[var(--color-muted)]">Run it remotely</p>
        <pre className="overflow-x-auto font-mono text-[13px] leading-6 text-[var(--color-foreground)]">
          <code>{iwrCommand}</code>
        </pre>
        <p className="text-sm text-[var(--color-muted)]">
          This fetches the script and runs it directly in your current PowerShell session — nothing is
          saved to disk. Only run it remotely if you trust the source; review the script first if you
          don&apos;t. To inspect it or run it locally instead, download <strong>Raw .ps1</strong> above,
          then run it from disk (you may need to <code>Unblock-File</code> it first, depending on your
          execution policy).
        </p>
      </div>
    </div>
  );
}
